import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { CopyJustification } from 'types/constants';
import { cn } from 'utils/styles';
import { CalloutGridCardProps } from 'libs/contentful/props';

/**
 * Props for the CalloutGridCardContent component
 */
interface CalloutGridCardContentProps {
  sys?: { id: string };
  card: CalloutGridCardProps;
  copyJustification?: CopyJustification;
  isLinkedCard: boolean;
}

/**
 * Renders the text content for a CalloutGrid card
 * Handles headline, copy, attribution, and justification
 */
const CalloutGridCardContent = ({ sys, card, copyJustification, isLinkedCard }: CalloutGridCardContentProps) => {
  const copyWrapperClasses = cn(
    copyJustification === CopyJustification.Center ? 'sm:text-center' : 'text-left',
    'sm:mt-4 flex flex-col gap-2',
  );

  // If there is no main headline, promote the small headline
  // so we don't skip from an H2 to an H4 in the document.
  const smallHeadlineLevel: 'h3' | 'h4' = card.headline ? 'h4' : 'h3';

  return (
    <div className={copyWrapperClasses}>
      {card.headline && (
        <div
          className={cn(
            'font-sans font-semibold text-heading-sm',
            isLinkedCard && 'relative flex flex-col items-center pr-3 underline',
          )}
          {...liveUpdateAttrs(sys?.id, 'headline')}>
          <RichTextHeadline headline={card.headline} level="h3" />
        </div>
      )}
      {card.headlineSmallRte && (
        <div
          className={cn('font-sans font-semibold text-body-sans', isLinkedCard && 'underline')}
          {...liveUpdateAttrs(sys?.id, 'headlineSmallRte')}>
          <RichTextHeadline headline={card.headlineSmallRte} level={smallHeadlineLevel} />
        </div>
      )}
      {card.copy && (
        <div className="font-serif text-body-serif" {...liveUpdateAttrs(sys?.id, 'copy')}>
          <RichTextCopy copy={card.copy} ySpacing="none" />
        </div>
      )}
      {card.attributionRte && (
        <div className="font-sans font-medium" {...liveUpdateAttrs(sys?.id, 'attributionRte')}>
          &mdash; <RichTextCopy copy={card.attributionRte} ySpacing="none" inline />
        </div>
      )}
    </div>
  );
};

export default CalloutGridCardContent;
